import set from 'lodash/set';

import { ColumnTypesInternal } from 'support/types/webediTable';

import { resolveModificationFieldKey, transformValue } from './helpers';
import { ColumnMetadata } from './types';

export const transformGroupedRows = (
  groupedRows: Array<Array<Record<string, unknown>>>,
  metadata: ColumnMetadata,
  sheets: Record<string, Array<Record<string, unknown>>>,
): Array<Record<string, unknown>> => {
  return groupedRows.map((rowGroup) => {
    const transformedRecord = transformMainColumns(rowGroup[0], metadata.mainColumns, sheets);

    metadata.embeddedTableGroups.forEach((group) => {
      const embeddedTableRows = transformEmbeddedTable(rowGroup, group, sheets);
      set(transformedRecord, group.parentKey, embeddedTableRows);
    });

    return transformedRecord;
  });
};

const transformMainColumns = (
  firstRow: Record<string, unknown>,
  mainColumns: ColumnMetadata['mainColumns'],
  sheets: Record<string, Array<Record<string, unknown>>>,
): Record<string, unknown> => {
  const transformedRecord: Record<string, unknown> = {
    _internal: { [ColumnTypesInternal.procuros_added]: true },
  };

  mainColumns.forEach((col) => {
    const value = firstRow[col.key];
    const transformedValue = transformValue(value, col.format, sheets, col.key);
    set(transformedRecord, col.key, transformedValue);
  });

  return transformedRecord;
};

const buildEmbeddedTableRow = (
  row: Record<string, unknown>,
  subColumns: ColumnMetadata['embeddedTableGroups'][0]['subColumns'],
  sheets: Record<string, Array<Record<string, unknown>>>,
): { embeddedTableRow: Record<string, unknown>; hasAnyValue: boolean } => {
  const embeddedTableRow: Record<string, unknown> = {};
  let hasAnyValue = false;

  subColumns.forEach((subCol) => {
    const value = row[subCol.key];
    const transformedValue = transformValue(value, subCol.format, sheets, subCol.key);
    const finalEmbeddedTableKey = resolveModificationFieldKey(subCol.key, subCol.embeddedTableKey, row, sheets);

    set(embeddedTableRow, finalEmbeddedTableKey, transformedValue);

    if (transformedValue !== null && transformedValue !== undefined) {
      hasAnyValue = true;
    }
  });

  return { embeddedTableRow, hasAnyValue };
};

const transformEmbeddedTable = (
  rowGroup: Array<Record<string, unknown>>,
  group: ColumnMetadata['embeddedTableGroups'][0],
  sheets: Record<string, Array<Record<string, unknown>>>,
): Array<Record<string, unknown>> => {
  const embeddedTableRows: Array<Record<string, unknown>> = [];
  const seenRows = new Set<string>();

  rowGroup.forEach((row) => {
    const { embeddedTableRow, hasAnyValue } = buildEmbeddedTableRow(row, group.subColumns, sheets);

    if (hasAnyValue) {
      const rowKey = JSON.stringify(embeddedTableRow);
      const isDuplicate = seenRows.has(rowKey);

      if (!isDuplicate) {
        embeddedTableRows.push(embeddedTableRow);
        seenRows.add(rowKey);
      }
    }
  });

  return embeddedTableRows;
};
